/**
 * "How this 8D is scored" — a collapsible guidance panel (CR-7356).
 *
 * Shows the 1–5 grading criteria for one or more scoring dimensions so a
 * supplier completing the 8D can see what each grade means. Read-only text;
 * collapsed by default so it never crowds the form. Reads its content from
 * the shared `scoreCriteria` definitions, the same source the score dropdowns
 * use.
 */

import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import type { ScoreDimension } from './scoreCriteria';

interface ScoreCriteriaPanelProps {
  /** Dimensions to show, in order (e.g. ORC then ERC). */
  dimensions: ScoreDimension[];
  /** Panel heading; defaults to "How this 8D is scored". */
  title?: string;
}

export function ScoreCriteriaPanel({
  dimensions,
  title = 'How this 8D is scored',
}: ScoreCriteriaPanelProps) {
  const [open, setOpen] = useState(false);

  if (dimensions.length === 0) return null;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-md border bg-muted/30"
    >
      <CollapsibleTrigger
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
        aria-label={title}
      >
        <span className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        <div className="space-y-4">
          {dimensions.map((dim) => (
            <div key={dim.key}>
              <p className="text-sm font-semibold">
                {dim.abbr} — {dim.name}
              </p>
              <ul className="mt-1.5 space-y-1.5">
                {dim.levels.map((lvl) => (
                  <li key={lvl.level} className="text-xs leading-snug">
                    <span className="font-medium">
                      {lvl.level}. {lvl.headline}
                    </span>
                    <span className="text-muted-foreground"> — {lvl.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
